import { RankingInfo } from '@tanstack/match-sorter-utils'
import {
    FilterFn,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table'

import { UsersIcon } from '@heroicons/react/24/solid'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { TeamView, useDeleteTeamMutation, useGetTeamsQuery } from '../../api/client'
import { toastError } from '../../components/Notification'
import DeleteTeamModal from '../../components/DeleteTeamModal'
import SearchInput from '../../components/SearchInput'
import Spinner from '../../components/Spinner'
import AdminActionBar from '../../components/admin/AdminActionBar'
import PageMasthead from '../../components/PageMasthead'
import SimplePagination from '../../components/table/SimplePagination'
import SimpleTable from '../../components/table/SimpleTable'
import { fuzzyFilter, fuzzySort } from '../../components/table/helpers'
import { monogram } from '../../util/monogram'
import {
    DeleteRowAction,
    RowActions,
    rowActionsHeader,
    ViewRowAction
} from '../../components/table/rowActions'

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const columnHelper = createColumnHelper<TeamView>()

function TeamAdminPage() {
    const location = useLocation()

    const { data: getTeamsData, loading: getTeamsLoading, refetch } = useGetTeamsQuery()

    const [deleteTeamMutation, { loading: deleteTeamLoading }] = useDeleteTeamMutation()

    const [forceDeleteTarget, setForceDeleteTarget] = useState<{
        teamId: number
        userCount: number
        accountCount: number
    } | null>(null)

    // When the location changes, e.g. navigating back from creating a new team, then we should refetch.
    useEffect(
        () => {
            refetch()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname]
    )

    async function deleteTeam(team: TeamView) {
        await deleteTeamMutation({
            variables: { teamId: team.id, force: false },
            onError: (error) => {
                try {
                    const parsed = JSON.parse(error.message)
                    if (parsed.type === 'TEAM_DELETION_BLOCKED') {
                        setForceDeleteTarget({
                            teamId: team.id,
                            userCount: parsed.userCount,
                            accountCount: parsed.accountCount,
                        })
                        return
                    }
                } catch (_) {}
                toastError(error.message)
            },
        })
        await refetch()
    }

    function ActionsCell(info: any) {
        const team = info.row.original as TeamView
        return (
            <RowActions>
                <ViewRowAction href={`#/admin/team/edit/${team.id}`} label="View team" />
                <DeleteRowAction
                    onClick={() => deleteTeam(team)}
                    label="Delete team"
                    disabled={deleteTeamLoading}
                />
            </RowActions>
        )
    }

    const columns = [
        columnHelper.accessor('name', {
            header: () => <div className="text-left">Team name</div>,
            cell: (info) => (
                <div className="flex items-center gap-2.5">
                    <span
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-ink-100 text-[11px] font-bold text-ink-800"
                        aria-hidden="true"
                    >
                        {monogram(info.getValue())}
                    </span>
                    <div>
                        <div className="text-sm font-medium text-ink-900">{info.getValue()}</div>
                        <div className="text-xs text-ink-500">ID: {info.row.original.id}</div>
                    </div>
                </div>
            ),
            filterFn: 'fuzzy',
            sortingFn: fuzzySort
        }),
        columnHelper.display({
            id: 'actions',
            header: rowActionsHeader,
            size: 20,
            cell: (props) => <ActionsCell row={props.row} />
        })
    ]

    const [globalFilter, setGlobalFilter] = useState('')
    const [data, setData] = useState<TeamView[]>([])

    useEffect(() => {
        if (getTeamsData?.getTeams) {
            setData(getTeamsData?.getTeams)
        }
    }, [getTeamsData])

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter
        },
        state: {
            globalFilter
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel()
    })

    const teamCount = getTeamsData?.getTeams.length || 0

    return (
        // Grey page backdrop behind the white list card, shared across admin lists.
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            {/* Header */}
            <PageMasthead
                title="Team management"
                tabTitle="Team management"
                subtitle={`${data.length} user${data.length !== 1 ? 's' : ''} on this page${teamCount > data.length ? ` of ${teamCount} total` : ''}`}
                actions={
                    <a
                        href="#/admin/team/create"
                        className="rounded-lg bg-arwen-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-arwen-700"
                    >
                        Create team
                    </a>
                }
            />

            {/* Total teams, previously shown as a quick stat in the header. */}
            <div className="flex items-center gap-1.5 px-6 pt-4 font-sans text-sm text-ink-500">
                <UsersIcon className="h-4 w-4" />
                <span>{teamCount} total</span>
            </div>
            <AdminActionBar>
                <div className="mr-4">
                    <SearchInput
                        keyword={globalFilter ?? ''}
                        onKeywordChange={(value) => setGlobalFilter(String(value))}
                        onSearch={(value) => setGlobalFilter(String(value))}
                    />
                </div>
                <div>{(getTeamsLoading || deleteTeamLoading) && <Spinner size={6} />}</div>
            </AdminActionBar>
            <div className="flex-1 px-6 py-4">
                <SimpleTable table={table} />
                <div className="pt-3">
                    <SimplePagination table={table} />
                </div>
            </div>
            {forceDeleteTarget && (
                <DeleteTeamModal
                    setVisible={() => setForceDeleteTarget(null)}
                    teamId={forceDeleteTarget.teamId}
                    userCount={forceDeleteTarget.userCount}
                    accountCount={forceDeleteTarget.accountCount}
                    onDeleted={async () => {
                        setForceDeleteTarget(null)
                        await refetch()
                    }}
                />
            )}
        </div>
    )
}

export default TeamAdminPage
